import React, { useState } from 'react';
import styles from '../../src/assets/scss/AddPermission.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreatepermissionMutation, useUpdatePermissionMutation } from '../slices/admin/admin';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import 'react-datepicker/dist/react-datepicker.css';

const AddPermission = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.state?.mode === 'edit';
    const formData = location.state?.data || {};

    const [createPermission] = useCreatepermissionMutation();
    const [updatePermission] = useUpdatePermissionMutation();


    // Form Fields
    const [permissionName, setPermissionName] = useState(formData.permissionName || '');
    const [description, setDescription] = useState(formData.description || '');
    const [effectiveDate, setEffectiveDate] = useState(
        formData.effectiveDate || ''
    );

    const [isActive, setIsActive] = useState(
        formData.isActive !== undefined
            ? formData.isActive
            : ''
    );
    const [permissionType, setPermissionType] = useState(formData.permissionType || '');
    const [functionality, setFunctionality] = useState(formData.functionality || '');


    // Error States
    const [errors, setErrors] = useState({});

    const handleCancel = () => {
        navigate(-1);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!permissionName.trim()) newErrors.permissionName = 'Permission name is required';
        if (!description.trim()) newErrors.description = 'Description is required';
        if (!effectiveDate) newErrors.effectiveDate = 'Effective date is required';
        if (!functionality) newErrors.functionality = 'Functionality is required';
        if (!permissionType) newErrors.permissionType = 'Permission type is required';
        if (!functionality) newErrors.functionality = 'Functionality is required';
        if (isActive === '') newErrors.isActive = 'Status is required';
        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };



    const handleSave = async () => {
        if (!validateForm()) return;

        const payload = {
            permissionName,
            description,
            isActive,
            permissionType,
            functionality,
            effectiveDate,
        };

        try {
            if (isEdit) {
                const id = formData._id;
                await updatePermission({ id, payload }).unwrap();
                toast.success('Permission updated successfully!');
            } else {
                await createPermission(payload).unwrap();
                toast.success('Permission saved successfully!');
            }
            navigate('/dashboard/Permissions');
        } catch (error) {
            const errMsg = error?.data?.message || 'Failed to save permission.';
            toast.error(errMsg);
        }
    };


    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.tittle}>
                    {isEdit ? 'Update Permission' : 'Add New Permission'}
                </h2>
                <span className={styles.closeIcon} onClick={handleCancel}>✕</span>
            </div>

            <div className={styles.child}>
                <div className={styles.form}>
                    {/* Permission Name */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Permission Name</label>
                        <input
                            className={styles.input}
                            type="text"
                            value={permissionName}
                            onChange={(e) => setPermissionName(e.target.value)}
                            placeholder="Store Owner"
                        />
                        {errors.permissionName && <p className={styles.error}>{errors.permissionName}</p>}
                    </div>

                    {/* Description */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Permission Description</label>
                        <input
                            className={styles.input}
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Site Owner Has Full Access"
                        />
                        {errors.description && <p className={styles.error}>{errors.description}</p>}
                    </div>

                    {/* Effective Date */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Effective Date</label>
                        <DatePicker
                            selected={effectiveDate ? dayjs(effectiveDate, 'YYYY-MM-DD').toDate() : null}
                            onChange={(date) => {
                                const formatted = dayjs(date).format('YYYY-MM-DD');
                                setEffectiveDate(formatted);
                            }}
                            dateFormat="MM.dd.yyyy"
                            minDate={new Date()}
                            placeholderText="Select date"
                            className={styles.input}
                            wrapperClassName={styles.fullWidthDatepicker}
                        />
                        {errors.effectiveDate && <p className={styles.error}>{errors.effectiveDate}</p>}
                    </div>


                    {/* Status */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Status</label>
                        <select
                            className={styles.inputs}
                            value={isActive === true ? 'Active' : isActive === false ? 'Inactive' : ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setIsActive(value === 'Active' ? true : value === 'Inactive' ? false : '');
                            }}
                        >
                            <option value="">Select Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                        {errors.isActive && <p className={styles.error}>{errors.isActive}</p>}
                    </div>

                    {/* Permission Type */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Permission Type</label>
                        <select
                            className={styles.inputs}
                            value={permissionType}
                            onChange={(e) => setPermissionType(e.target.value)}
                        >
                            <option value="">Select Type</option>
                            <option value="fullaccess">Full Access</option>
                            <option value="update">Update</option>
                            <option value="create">Create</option>
                            <option value="view only">View Only</option>
                            <option value="status update">Status Update</option>
                        </select>
                        {errors.permissionType && (
                            <p className={styles.error}>{errors.permissionType}</p>
                        )}
                    </div>


                    {/* Functionality */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Functionality</label>
                        <select
                            className={styles.inputs}
                            value={functionality}
                            onChange={(e) => setFunctionality(e.target.value)}
                        >
                            <option value="">Select Functionality</option>
                            <option value="billing">Billing</option>
                            <option value="ledger">Ledger</option>
                            <option value="expense">Expense</option>
                            <option value="inventory">Inventory</option>
                            <option value="reports">Reports</option>
                            <option value="invoice">Invoice</option>
                            <option value="orders">Orders</option>
                            <option value="dashboard">Dashboard</option>
                            <option value="Admin">Admin</option>
                            <option value="usercontroll">User Controll</option>
                            <option value="invite">Invite</option>
                            <option value="categories">Item Categories</option>
                            <option value="helpcenteruser">User Help Center</option>
                            <option value="helpcenteradmin">Admin Help Center </option>
                            <option value="plan">Plan</option>
                            <option value="subscriptionAdmin">Subscription Admin</option>
                            <option value="subscriptionUsers">Subscription User</option>
                            <option value="transportHandlers">Transport & Handlers </option>
                            <option value="paymentSettings">Payment Settings</option>
                             <option value="landingPageEnquiry">Landing Page Enquiry</option>
                            <option value="downloadHistory">Download History</option>
                        </select>
                        {errors.functionality && (
                            <p className={styles.error}>{errors.functionality}</p>
                        )}
                    </div>

                </div>

                <div className={styles.buttonGroup}>
                    <button className={styles.cancel} onClick={handleCancel}>Cancel</button>
                    <button className={styles.save} onClick={handleSave}>
                        {isEdit ? 'Update' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddPermission;


